import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  getApiV1IndividualpaymentParcelsParcelidInvoicesQueryKey,
  usePostApiV1IndividualpaymentParcelsParcelidInvoiceUpload,
} from '@/shared/api/generated';

import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/** Extensões que o backend aceita, no formato do atributo `accept`. */
export const INVOICE_UPLOAD_ACCEPT = '.pdf,.png,.jpg,.jpeg,.bmp,.tiff,.tif';

/**
 * Envio do arquivo da nota fiscal de uma parcela.
 *
 * O cliente gerado monta o `multipart/form-data` a partir de `{ file }` — o
 * componente só entrega o `File`, sem tocar em `FormData`.
 *
 * Duas queries são invalidadas no sucesso: a lista de comprovantes da parcela,
 * porque o anexo vira uma linha nela, e nada mais — o upload não altera valores
 * nem vencimento, então o header do contrato não muda.
 *
 * O `400` deste endpoint tem um significado específico que a tela precisa
 * repassar: além de arquivo inválido, ele é a recusa por **NFe em processamento**
 * na mesma parcela. É regra de negócio, e a mensagem do backend é o que o
 * operador precisa ler — daí a mesma classificação das demais escritas da
 * feature.
 *
 * @param parcelId - ID da parcela; `null` torna o `upload` inerte.
 * @returns `{ upload, isPending, error, isServerError, isForbidden, reset }`
 *
 * @example
 * ```tsx
 * const { upload, isPending } = useUploadParcelInvoice(42);
 * upload(file, { onSuccess: () => close() });
 * ```
 */
export function useUploadParcelInvoice(parcelId: number | null) {
  const queryClient = useQueryClient();
  const mutation = usePostApiV1IndividualpaymentParcelsParcelidInvoiceUpload();

  const upload = useCallback(
    (
      file: File,
      options?: {
        readonly onSuccess?: () => void;
        readonly onError?: (error: PlanWriteError) => void;
      },
    ) => {
      if (parcelId === null) return;

      mutation.mutate(
        { parcelId, data: { file } },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: getApiV1IndividualpaymentParcelsParcelidInvoicesQueryKey(parcelId),
            });
            options?.onSuccess?.();
          },
          onError: error => options?.onError?.(classifyPlanWriteError(error)),
        },
      );
    },
    [parcelId, mutation, queryClient],
  );

  const failure = classifyPlanWriteError(mutation.error);

  return {
    upload,
    isPending: mutation.isPending,
    isError: mutation.isError,
    /** Falha de regra de negócio, com a mensagem do backend quando houver. */
    error: failure,
    isForbidden: failure.kind === 'forbidden',
    isServerError: failure.kind === 'server',
    reset: mutation.reset,
  } as const;
}
